import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  formatLapTime,
  formatNumber,
  formatTemperature,
  type TemperatureUnit,
} from "../lib/format";
import { splitStints } from "../lib/stints";
import type { Lap, TelemetrySession } from "../lib/types";

interface Props {
  session: TelemetrySession;
  laps: Lap[];
}

const UNIT_KEY = "lmustats.temperature";

/** Wetness above this is a wet track rather than a damp patch somewhere. */
const WET_THRESHOLD = 0.1;

/**
 * What it was like out there, and what the run cost.
 *
 * Four figures on one line, and the stint table only when there was more than one
 * stint — a single-stint practice session gets its numbers in the tiles and no
 * table at all, because a table of one row is a worse way to read one row.
 *
 * The consumables are deliberately quoted per stint rather than per session.
 * Fuel used across a session that was refuelled says something about the pit
 * crew; fuel per lap within a stint says something about the driver, and only the
 * second is worth the space.
 */
export function SessionConditions({ session, laps }: Props) {
  const { t } = useTranslation();
  const [unit, setUnit] = useState<TemperatureUnit>(
    () => (localStorage.getItem(UNIT_KEY) as TemperatureUnit | null) ?? "c",
  );

  function pickUnit(next: TemperatureUnit) {
    localStorage.setItem(UNIT_KEY, next);
    setUnit(next);
  }

  const stints = splitStints(laps);
  const running = stints.filter((stint) => stint.laps.length > 1);
  const wet = session.wetness !== null && session.wetness >= WET_THRESHOLD;

  const fuel = sum(laps.map((lap) => lap.fuel_used_l));
  const tyre = sum(laps.map((lap) => lap.tyre_used_pct));
  const treadLeft = lastLeft(laps);

  // Nothing recorded any of it: an older session, from before these were read.
  if (
    session.air_temp_c === null &&
    session.track_temp_c === null &&
    fuel === null &&
    tyre === null
  ) {
    return null;
  }

  return (
    <section className="mt-6 overflow-hidden rounded-xl border border-zinc-800">
      <div className="grid divide-y divide-zinc-800 sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-4 lg:divide-x lg:divide-zinc-800">
        <Tile
          label={t("conditions.weather")}
          value={
            <span className="flex items-baseline gap-2">
              {/* Sessions ingested before the temperatures were read still have
                  LMU's own caption, which is better than an em dash. */}
              <span className={session.air_temp_c === null ? "text-base" : undefined}>
                {session.air_temp_c === null
                  ? (session.weather ?? "—")
                  : formatTemperature(session.air_temp_c, unit)}
              </span>
              {/* Silent rather than "Dry" when nothing recorded it: an unknown is
                  not a dry track. */}
              {session.wetness !== null && (
                <span
                  className={[
                    "rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider",
                    wet ? "bg-sky-500/15 text-sky-300" : "bg-zinc-800 text-zinc-400",
                  ].join(" ")}
                >
                  {t(wet ? "conditions.wet" : "conditions.dry")}
                </span>
              )}
            </span>
          }
          detail={
            session.track_temp_c !== null
              ? t("conditions.trackTemp", {
                  value: formatTemperature(session.track_temp_c, unit),
                })
              : (session.weather ?? undefined)
          }
          action={
            session.air_temp_c !== null || session.track_temp_c !== null ? (
              <div className="flex overflow-hidden rounded border border-zinc-700 text-[10px]">
                {(["c", "f"] as const).map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => pickUnit(code)}
                    className={[
                      "px-1.5 py-0.5 font-medium uppercase transition-colors",
                      unit === code
                        ? "bg-zinc-700 text-zinc-100"
                        : "text-zinc-500 hover:text-zinc-300",
                    ].join(" ")}
                  >
                    °{code}
                  </button>
                ))}
              </div>
            ) : undefined
          }
        />

        <Tile
          label={t("conditions.fuel")}
          value={fuel === null ? "—" : t("conditions.litres", { value: formatNumber(fuel, 1) })}
          detail={
            running.length > 0 && running[0].fuelPerLapL !== null
              ? t("conditions.perLap", {
                  value: formatNumber(running[0].fuelPerLapL, 2),
                })
              : undefined
          }
        />

        <Tile
          label={t("conditions.tyres")}
          value={tyre === null ? "—" : t("conditions.percentUsed", { value: formatNumber(tyre, 1) })}
          detail={
            treadLeft !== null
              ? t("conditions.treadLeft", { value: formatNumber(treadLeft, 1) })
              : undefined
          }
        />

        <Tile
          label={t("conditions.pitStops")}
          value={String(session.pit_stops)}
          detail={
            session.pit_stops === 0
              ? t("conditions.singleStint")
              : t("conditions.stintCount", { count: running.length })
          }
        />
      </div>

      {running.length > 1 && (
        <div className="border-t border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-2 font-medium">{t("conditions.columns.stint")}</th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("conditions.columns.laps")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("conditions.columns.best")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("conditions.columns.degradation")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("conditions.columns.fuel")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("conditions.columns.tyres")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {running.map((stint) => (
                <tr key={stint.number}>
                  <td className="px-3 py-2 text-zinc-300">
                    <span className="tabular-nums">
                      {t("conditions.lapRange", {
                        from: stint.firstLap,
                        to: stint.lastLap,
                      })}
                    </span>
                    {stint.compoundChanged && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-400">
                        {t("conditions.newTyres")}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-400">
                    {stint.laps.length}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-200">
                    {formatLapTime(stint.bestLapMs)}
                  </td>
                  <td
                    className={[
                      "px-3 py-2 text-right tabular-nums",
                      stint.degradationSPerLap === null
                        ? "text-zinc-600"
                        : stint.degradationSPerLap > 0.02
                          ? "text-amber-400"
                          : "text-zinc-400",
                    ].join(" ")}
                  >
                    {stint.degradationSPerLap === null
                      ? t("conditions.tooShort")
                      : t("conditions.perLapSeconds", {
                          value: signed(stint.degradationSPerLap),
                        })}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-400">
                    {stint.fuelPerLapL === null
                      ? "—"
                      : t("conditions.litresPerLap", {
                          value: formatNumber(stint.fuelPerLapL, 2),
                        })}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-400">
                    {stint.tyreUsedPct === null
                      ? "—"
                      : t("conditions.percentUsed", {
                          value: formatNumber(stint.tyreUsedPct, 1),
                        })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

interface TileProps {
  label: string;
  value: React.ReactNode;
  detail?: string;
  action?: React.ReactNode;
}

function Tile({ label, value, detail, action }: TileProps) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs uppercase tracking-wider text-zinc-500">{label}</span>
        {action}
      </div>
      <div className="mt-1 text-lg text-zinc-100">{value}</div>
      <p className="mt-0.5 h-4 text-xs text-zinc-500">{detail ?? ""}</p>
    </div>
  );
}

function signed(seconds: number): string {
  return `${seconds > 0 ? "+" : ""}${formatNumber(seconds, 2)}`;
}

function sum(values: Array<number | null>): number | null {
  const present = values.filter((value): value is number => value !== null);
  return present.length > 0 ? present.reduce((total, value) => total + value, 0) : null;
}

function lastLeft(laps: Lap[]): number | null {
  for (let index = laps.length - 1; index >= 0; index -= 1) {
    const value = laps[index].tyre_left_pct;
    if (value !== null) return value;
  }
  return null;
}
